/**
 * @fileoverview Stadium Giant Screen Game Component for 'Cronómetro' (ADSL Tile ID 50) on stadium LED screens (/screen).
 * 100% faithful to the official prototype screen.html:
 * - Centered top game logo with neon cyan drop-shadow.
 * - Central yellow-bordered glow objective card with 60 FPS CSS keyframe animations (objective-pulse, yellow-box-border-cycle, number-lively-dance).
 * - Live qualified spectators container (.stats-container) with blue-box-border-cycle neon glow and .stat-winner-count.
 * - STRICT VISUAL PRIVACY: At no moment are running seconds/milliseconds displayed on the stadium screen.
 * 
 * @module components/Screen/Sections/Cronometer/CronometerGame
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { GetPublicConfig } from '../../../../utilities/DL/Cronometer';

const DEFAULT_BACKGROUND = '/img/games/cronometer/background-1.png';
const DEFAULT_GAME_LOGO = '/img/games/cronometer/logo-cronometro.png';

/**
 * Resolves media paths to fully qualified URLs when needed.
 * 
 * @param {string|null} mediaPath - Relative or absolute path to asset.
 * @param {string} fallbackPath - Default fallback image.
 * @returns {string} Fully resolved image URL.
 */
const resolveMediaUrl = (mediaPath, fallbackPath) => {
    if (!mediaPath) return fallbackPath;
    if (
        mediaPath.startsWith('http://') ||
        mediaPath.startsWith('https://') ||
        mediaPath.startsWith('blob:') ||
        mediaPath.startsWith('data:')
    ) {
        return mediaPath;
    }
    const apiBase = (process.env.REACT_APP_API_URL || '').replace(/\/api\/?$/, '');
    const cleanPath = mediaPath.startsWith('/') ? mediaPath : `/${mediaPath}`;
    return apiBase ? `${apiBase}${cleanPath}` : cleanPath;
};

/**
 * CronometerGame Component (/screen - Componente 50.1)
 * 
 * @function CronometerGame
 * @param {Object} props - Component properties.
 * @param {Object} [props.payload] - WebSocket payload from Screen router.
 * @returns {React.ReactElement} The rendered stadium game view.
 */
export const CronometerGame = ({ payload }) => {
    // =========================================================================
    // 1. ESTADOS PRINCIPALES DE JUEGO (IDÉNTICOS A SCREEN.HTML)
    // =========================================================================
    const [currentPhase, setCurrentPhase] = useState(1);
    const [targetZ, setTargetZ] = useState(9);
    const [objectiveRevealed, setObjectiveRevealed] = useState(false);
    const [isObjectiveExpired, setIsObjectiveExpired] = useState(false);
    const [isCycling, setIsCycling] = useState(false);
    const [winnersByPhase, setWinnersByPhase] = useState({ 1: 0 });
    const [themeData, setThemeData] = useState(null);

    // Referencia al temporizador de animación del objetivo Z
    const animationTimerRef = useRef(null);

    // =========================================================================
    // 2. RECUPERACIÓN DE TEMA VISUAL (BACKGROUND & LOGO)
    // =========================================================================
    useEffect(() => {
        const fetchTheme = async () => {
            try {
                const res = await GetPublicConfig();
                if (res && res.success && res.data) {
                    if (res.data.theme) {
                        setThemeData(res.data.theme);
                    } else if (res.data.activeTheme) {
                        setThemeData(res.data.activeTheme);
                    }
                }
            } catch (e) {
                // Non-blocking fallback
            }
        };

        if (payload?.theme || payload?.currentTheme || payload?.data?.theme) {
            setThemeData(payload.theme || payload.currentTheme || payload.data?.theme);
        } else {
            fetchTheme();
        }
    }, [payload]);

    // Limpieza de temporizadores al desmontar
    useEffect(() => {
        return () => {
            if (animationTimerRef.current) {
                clearTimeout(animationTimerRef.current);
                animationTimerRef.current = null;
            }
        };
    }, []);

    // =========================================================================
    // 3. PROCESAMIENTO REACTIVO DE MENSAJES WEBSOCKET
    // =========================================================================
    useEffect(() => {
        if (!payload) return;

        const eventType = (payload.type || payload.Type || '').toLowerCase();
        const data = payload.payload !== undefined
            ? payload.payload
            : (payload.data !== undefined ? payload.data : payload);

        switch (eventType) {
            case 'crono_show_objective': {
                if (animationTimerRef.current) {
                    clearTimeout(animationTimerRef.current);
                    animationTimerRef.current = null;
                }
                setIsCycling(false);
                setIsObjectiveExpired(false);
                setObjectiveRevealed(true);
                const zVal = data?.z !== undefined
                    ? data.z
                    : (payload.z !== undefined ? payload.z : 9);
                setTargetZ(zVal);
                break;
            }

            case 'crono_start_phase': {
                if (animationTimerRef.current) {
                    clearTimeout(animationTimerRef.current);
                    animationTimerRef.current = null;
                }

                setIsObjectiveExpired(false);
                setObjectiveRevealed(true);

                const phaseVal = data?.fase !== undefined
                    ? data.fase
                    : (data?.phase !== undefined ? data.phase : (payload.fase || payload.phase || 1));
                const parsedPhase = parseInt(phaseVal, 10) || 1;
                setCurrentPhase(parsedPhase);

                const zNum = data?.z !== undefined
                    ? parseFloat(data.z)
                    : (payload.z !== undefined ? parseFloat(payload.z) : 9);
                setTargetZ(zNum);

                // Activar animación viva del número y ciclos de color
                setIsCycling(true);

                // Temporizador exacto para la duración del objetivo (Z segundos)
                const durationMs = (zNum || 9) * 1000;
                animationTimerRef.current = setTimeout(() => {
                    setIsCycling(false);
                    setIsObjectiveExpired(true);
                }, durationMs);
                break;
            }

            case 'crono_objective_expired': {
                if (animationTimerRef.current) {
                    clearTimeout(animationTimerRef.current);
                    animationTimerRef.current = null;
                }
                setIsCycling(false);
                setIsObjectiveExpired(true);
                break;
            }

            case 'crono_end_phase': {
                if (animationTimerRef.current) {
                    clearTimeout(animationTimerRef.current);
                    animationTimerRef.current = null;
                }
                setIsCycling(false);
                setIsObjectiveExpired(true);

                if (data?.phaseWinners) {
                    setWinnersByPhase(data.phaseWinners);
                }
                if (data?.phase) {
                    setCurrentPhase(parseInt(data.phase, 10) || 1);
                }
                break;
            }

            case 'crono_change_phase': {
                const phaseVal = data?.fase !== undefined
                    ? data.fase
                    : (data?.phase !== undefined ? data.phase : (payload.fase || payload.phase || payload.currentPhase || 1));
                setCurrentPhase(parseInt(phaseVal, 10) || 1);
                setIsCycling(false);
                setIsObjectiveExpired(true);
                break;
            }

            case 'crono_update_spectator': {
                if (data?.phaseWinners) {
                    setWinnersByPhase(data.phaseWinners);
                }
                if (data?.phase) {
                    setCurrentPhase(parseInt(data.phase, 10) || 1);
                }
                break;
            }

            case 'crono_reset_game':
            case 'crono_reset': {
                if (animationTimerRef.current) {
                    clearTimeout(animationTimerRef.current);
                    animationTimerRef.current = null;
                }
                setIsCycling(false);
                setIsObjectiveExpired(true);
                setObjectiveRevealed(false);
                setCurrentPhase(1);
                setTargetZ(9);
                setWinnersByPhase({ 1: 0 });
                break;
            }

            case 'crono_game_state': {
                if (data?.phase) {
                    setCurrentPhase(parseInt(data.phase, 10) || 1);
                }
                if (data?.targetZ !== undefined) {
                    setTargetZ(data.targetZ);
                }
                if (data?.objectiveRevealed !== undefined) {
                    setObjectiveRevealed(Boolean(data.objectiveRevealed));
                }
                if (data?.phaseWinners) {
                    setWinnersByPhase(data.phaseWinners);
                }
                break;
            }

            case 'crono_change_phase': {
                if (animationTimerRef.current) {
                    clearTimeout(animationTimerRef.current);
                    animationTimerRef.current = null;
                }
                setIsCycling(false);
                setIsObjectiveExpired(true);
                if (data?.phase || data?.fase) {
                    setCurrentPhase(parseInt(data.phase || data.fase, 10) || 1);
                }
                break;
            }

            default:
                break;
        }
    }, [payload]);

    // =========================================================================
    // 4. CÁLCULO DE FILAS DE ESTADÍSTICAS (ÚLTIMAS 3 FASES DISPUTADAS)
    // =========================================================================
    const statsRows = useMemo(() => {
        const parsedPhase = parseInt(currentPhase, 10) || 1;
        const winnerPhases = Object.keys(winnersByPhase || {})
            .map(Number)
            .filter(n => !isNaN(n));
        const maxPhase = Math.max(parsedPhase, ...winnerPhases, 1);
        const startPhase = Math.max(1, maxPhase - 2);

        const rows = [];
        for (let i = startPhase; i <= maxPhase; i++) {
            const count = (winnersByPhase && winnersByPhase[i] !== undefined)
                ? winnersByPhase[i]
                : (winnersByPhase && winnersByPhase[String(i)] !== undefined ? winnersByPhase[String(i)] : 0);
            const label = count === 1 ? 'Jugador' : 'Jugadores';
            rows.push({ phase: i, count, label });
        }
        return rows;
    }, [currentPhase, winnersByPhase]);

    // Cifra visible del objetivo (estricta privacidad: '???' si no revelado o expirado)
    const displayedObjective = (!objectiveRevealed || isObjectiveExpired) ? '???' : targetZ;

    const backgroundUrl = resolveMediaUrl(
        themeData?.coverBackgroundUrl || themeData?.backgroundUrl,
        DEFAULT_BACKGROUND
    );
    const gameLogoUrl = resolveMediaUrl(
        themeData?.brandLogoUrl || themeData?.logoUrl,
        DEFAULT_GAME_LOGO
    );

    // =========================================================================
    // 5. RENDERIZADO VISUAL A 60 FPS (ESTRUCTURA FIDELÍSIMA A SCREEN.HTML)
    // =========================================================================
    return (
        <main
            className="cronometer-screen-game"
            role="region"
            aria-label="Escenario de Juego Cronómetro en Pantalla Gigante"
        >
            {/* Capa de imagen de fondo panorámico oscurecida/opacada al 75% */}
            <div
                id="bg-layer"
                className="bg-layer"
                style={{
                    backgroundImage: `url(${backgroundUrl})`,
                    display: 'block'
                }}
            />

            {/* Logotipo centrado en la parte superior con drop-shadow neón */}
            <img
                id="game-logo"
                className="cronometer-game-logo"
                src={gameLogoUrl}
                alt="Logotipo Cronómetro"
                onError={(e) => {
                    if (e.currentTarget.src !== DEFAULT_GAME_LOGO) {
                        e.currentTarget.src = DEFAULT_GAME_LOGO;
                    }
                }}
            />

            {/* Recuadro amarillo con "OBJETIVO" y el número PROTAGONISTA GIGANTE */}
            <div className="objective-row" id="objectiveRow">
                <div
                    className={`objective-badge-glow ${isCycling ? 'badge-color-cycling' : ''}`}
                    id="objectiveBadge"
                >
                    <span className="objective-label">Objetivo</span>
                    <span
                        id="objectiveNumber"
                        className={`objective-number ${isCycling ? 'number-alive' : ''}`}
                    >
                        {displayedObjective}
                    </span>
                </div>
            </div>

            {/* Cuadro de clasificados por Fase (con ciclo cromático en fase activa) */}
            <div
                className={`stats-container ${isCycling ? 'stats-color-cycling' : ''}`}
                id="stats"
            >
                {statsRows.map((row) => (
                    <div key={row.phase} className="stat-row">
                        <span>Clasificados Fase {row.phase}</span>
                        <span className="stat-winner-count">
                            {row.count} {row.label}
                        </span>
                    </div>
                ))}
            </div>
        </main>
    );
};

CronometerGame.propTypes = {
    payload: PropTypes.shape({
        type: PropTypes.string,
        mode: PropTypes.string,
        fase: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        phase: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        z: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        targetZ: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        margin: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        intermitente: PropTypes.bool,
        objectiveRevealed: PropTypes.bool,
        phaseWinners: PropTypes.object,
        data: PropTypes.object,
        payload: PropTypes.object,
        theme: PropTypes.object
    })
};

CronometerGame.defaultProps = {
    payload: null
};

export default CronometerGame;
